import React, { useState } from 'react';
import { Form, FormField, FormRow } from 'components/common/forms';
import { Quote, Reservation } from 'models';
import { useGlobalContext } from 'state/GlobalContext';
import { useVehicleOps } from 'hooks/useVehicleOps';
import DateRangeFiled from 'page-components/DateRangeFiled';
import GuestDropdown from 'page-components/GuestDropdown';
import Section from 'page-components/Section';
import Services from 'services/Services';
import { useLookups } from 'hooks/useLookups';
import { Heading } from 'theme-ui';
import useMount from 'hooks/useMount';

type Props = {
  onChange(value: Reservation): void;
}
const BookingForm: React.FC<Props> = ({ onChange }) => {
  const fields = Reservation.Model.fields;
  const init = Reservation.Model.initialValues;
  const [{ state, dispatch }] = useGlobalContext();
  const [form] = Form.useForm<Reservation>();
  const [vehicleOps] = useVehicleOps();
  const {siteTypes, siteTypeOps} = useLookups();
  const [hasVD, setHasVD] = useState(true);

  const fetchData = async (model: Reservation) => {
    await getQuote(model);
    dispatchSiteType(model?.siteTypeId);
  }

  useMount(() => {
    const model = Reservation.new(state.reservation);
    if (state.reservation) {
      form.setFieldsValue(model);
    }
    if (state.selectedPark) {
      fetchData(model);
    }
  });

  const updateVehicleDetails = (siteTypeId: string) => {
    const siteType = siteTypes.find(x => x.id === siteTypeId);
    if (siteType) {
      setHasVD(siteType.universalSiteType.hasVehicleDetails);
      setTimeout(() => {
        const formValues = form.getFieldsValue();
        onChange(formValues);
      }, 10);
    }
  }

  const onValuesChange = async (changedValues: Partial<Reservation>, values: Reservation) => {
    const fieldName = Object.keys(changedValues)[0];
    if (fieldName === fields.siteTypeId.name) {
      dispatchSiteType(values.siteTypeId);
      updateVehicleDetails(values.siteTypeId);
    }

    const formValues = form.getFieldsValue();
    onChange(formValues);

    await getQuote(formValues);
  };

  const dispatchSiteType = (id: string) => {
    if (!id) return;
    const siteType = siteTypes.find(x => x.id === id);
    dispatch({ type: 'SET_SITE_TYPE', payload: { siteType }});
  }

  const getQuote = async (reservation: Reservation) => {
    const condition = [
      reservation.dateRange,
      reservation.visitorsInfo,
      reservation.siteTypeId,
    ].map(val => Boolean(val)).every(x => x && true);

    const longTerm = Reservation.Model.isLongTerm(reservation);

    if (condition && !longTerm) {
      const payload = Quote.Model.buildPayload(reservation);
      const result = await Services.Reservations.getQuote(payload);
      if (result) {
        const quote = Quote.Model.mapModel(result);
        dispatch({ type: 'SET_QUOTE', payload: { quote: quote }});
      }
    }
    else if (state.quote) {
      dispatch({ type: 'SET_QUOTE', payload: { quote: null }});
    }
  };

  const onFormBlur = () => {
    const values = form.getFieldsValue();
    dispatch({ type: 'SET_RESERVATION', payload: { reservation: values }});
  }

  const onFinish = (values: Reservation) => {
    console.log(values);
  }

  return (
    <Form form={form}
      layout="vertical"
      initialValues={init}
      onFinish={onFinish}
      onBlur={onFormBlur}
      onValuesChange={onValuesChange}
    >
      <Heading>Booking Details</Heading>
      <FormField field={fields.id} hidden />

      <Section title="Reservation details" maxW={306}>
        <FormField field={fields.dateRange}>
          <DateRangeFiled />
        </FormField>
        <FormField field={fields.visitorsInfo}>
          <GuestDropdown />
        </FormField>
        <FormField field={fields.siteTypeId} options={siteTypeOps} />
      </Section>

      {hasVD && (
        <Section title="Equipment details" maxW={400}>
          <FormRow colSpans={[12, 12]} gap={16}>
            <FormField field={fields.vehicleType} options={vehicleOps.typeOps} />
            <FormField field={fields.vehicleLengthRange} options={vehicleOps.lengthRangeOps} />
          </FormRow>
          <FormRow colSpans={[16, 8]} gap={16}>
            <FormField field={fields.vehicleSlides} options={vehicleOps.slideOps} />
            <FormField field={fields.vehicleTowing} options={vehicleOps.towingOps} />
          </FormRow>
          <FormField field={fields.vehicleElectrical} options={vehicleOps.electricalOps} />
          <FormField field={fields.notes} />
        </Section>
      )}
    </Form>
  );
};

export default BookingForm;
